// src/pages/ClientConfigurationPage.jsx - MANUAL FIREBASE AUTH SETUP
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getClientById,
  updateOrganisationConfig,
  addAdminToClient,
  updateAdminUser,
  deleteAdminUser,
  updateClientModules,
  MODULES,
  AI_SUBMODULES,
  DEFAULT_MODULES,
} from '../api/superAdminApi';
import { DEFAULT_PERMISSIONS } from '../api/orgAuthService';
import '../styles/superadmin/sa-base.css';
import '../styles/superadmin/sa-header.css';

const ROLE_HELP = {
  owner:   'Full access to all features.',
  admin:   'Full access except User Management.',
  manager: 'Access to Community, Events and Reports.',
};

function ClientConfigurationPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('modules');
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [status, setStatus] = useState('active');

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getClientById(clientId);
      setClientData(data);
      setModules({ ...DEFAULT_MODULES, ...data.enabledModules });
      setStatus(data.status);
    } catch (err) {
      console.error('Error loading client data:', err);
      setError('Failed to load client configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToClients = () => {
    navigate('/superadmin/dashboard', { state: { activeTab: 'clients' } });
  };

  const handleAddAdmin = async (adminData) => {
    try {
      setSaving(true);
      setError('');
      await addAdminToClient(clientId, adminData);
      setShowAddAdminModal(false);
      setSuccessMessage('Admin user added successfully to the staff collection!');
      await loadClientData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error adding admin:', err);
      setError('Failed to add admin user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditAdmin = async (adminData) => {
    try {
      setSaving(true);
      setError('');
      await updateAdminUser(selectedAdmin.uid, adminData);
      setShowEditAdminModal(false);
      setSelectedAdmin(null);
      setSuccessMessage('Admin user updated successfully');
      await loadClientData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating admin:', err);
      setError('Failed to update admin user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (adminUid, adminName) => {
    if (!window.confirm(`Are you sure you want to delete admin user "${adminName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await deleteAdminUser(adminUid);
      setSuccessMessage('Admin user deleted successfully');
      await loadClientData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError('Failed to delete admin user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Maps each parent module to the AI sub-module that depends on it
  const AI_PARENT_DEPS = Object.fromEntries(AI_SUBMODULES.map(a => [a.parentKey, a.key]));

  const handleModuleToggle = (moduleName) => {
    setModules(prev => {
      const next = { ...prev, [moduleName]: !prev[moduleName] };
      // If a parent module is being disabled, also disable its dependent AI sub-module
      if (prev[moduleName] && AI_PARENT_DEPS[moduleName]) {
        next[AI_PARENT_DEPS[moduleName]] = false;
      }
      // If BespokeAI itself is being disabled, clear all AI sub-modules
      if (moduleName === 'bespokeai' && prev.bespokeai) {
        AI_SUBMODULES.forEach(a => { next[a.key] = false; });
      }
      return next;
    });
  };

  const handleAISubModuleToggle = (subModuleKey, parentKey) => {
    if (!modules[parentKey]) return; // guard: parent must be enabled
    setModules(prev => ({ ...prev, [subModuleKey]: !prev[subModuleKey] }));
  };

  const handleSaveModules = async () => {
    try {
      setSaving(true);
      setError('');
      await updateClientModules(clientId, modules);
      setSuccessMessage('Modules updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating modules:', err);
      setError('Failed to update modules: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    const previous = status;
    setStatus(newStatus);
    try {
      setError('');
      await updateOrganisationConfig(clientId, { status: newStatus });
      setSuccessMessage('Client status updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setStatus(previous);
      setError('Failed to update status: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="sa-portal">
        <div className="config-page-container">
          <div className="loading-spinner">Loading client configuration...</div>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="sa-portal">
        <div className="config-page-container">
          <div className="error-message">Client not found</div>
          <button onClick={handleBackToClients} className="back-button">
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const mainModuleKeys = ['programmes', 'community', 'supportmgmt', 'events', 'communications', 'reports', 'donors', 'bespokeai'];

  return (
    <div className="sa-portal">
    <div className="config-page-container">
      <div className="config-page-header">
        <div className="config-header-left">
          <button onClick={handleBackToClients} className="back-button">
            ← Back to Clients
          </button>
          <h1>{clientData.name} - Configuration</h1>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="config-tabs">
        <button
          className={`config-tab ${activeTab === 'modules' ? 'active' : ''}`}
          onClick={() => setActiveTab('modules')}
        >
          Modules
        </button>
        <button
          className={`config-tab ${activeTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveTab('admins')}
        >
          Admin Users
        </button>
        <button
          className={`config-tab ${activeTab === 'urls' ? 'active' : ''}`}
          onClick={() => setActiveTab('urls')}
        >
          Portal URLs
        </button>
      </div>

      <div className="config-content">
        {activeTab === 'modules' && (
          <div className="modules-tab-content">
            <div className="tab-header">
              <h2>Enabled Modules</h2>
              <button
                onClick={handleSaveModules}
                className="save-modules-button"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            <div className="modules-list-container">
              <p className="modules-description">
                Select which modules are enabled for this client. The Core module is always enabled.
              </p>

              <div className="modules-checkbox-list">
                <div className="module-checkbox-item">
                  <label className="module-checkbox-label">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      className="module-checkbox-input"
                    />
                    <span className="module-name">Core</span>
                    <span className="module-tag always-enabled">Always Enabled</span>
                  </label>
                  <p className="module-description-text">
                    Core organisation management features including branding, about us, contact details, images and typography.
                  </p>
                </div>

                {MODULES.map(m => (
                  <div className="module-checkbox-item" key={m.id}>
                    <label className="module-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!modules[m.id]}
                        onChange={() => handleModuleToggle(m.id)}
                        className="module-checkbox-input"
                      />
                      <span className="module-name">{m.label}</span>
                      {m.premium && <span className="module-tag premium">Premium</span>}
                    </label>
                    <p className="module-description-text">{m.description}</p>

                    {m.id === 'bespokeai' && modules.bespokeai && (
                      <div className="ai-submodules-section">
                        <p className="ai-submodules-heading">Select AI modules to enable:</p>
                        {AI_SUBMODULES.map(({ key, label, parentKey, parentLabel, description }) => {
                          const parentEnabled = modules[parentKey];
                          return (
                            <div
                              key={key}
                              className={`ai-submodule-item${!parentEnabled ? ' ai-submodule-disabled' : ''}`}
                            >
                              <label className="module-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={!!modules[key]}
                                  onChange={() => handleAISubModuleToggle(key, parentKey)}
                                  disabled={!parentEnabled}
                                  className="module-checkbox-input"
                                />
                                <span className="module-name ai-submodule-name">{label}</span>
                                {!parentEnabled && (
                                  <span className="module-tag ai-locked">Requires {parentLabel}</span>
                                )}
                              </label>
                              <p className="module-description-text" style={{ marginLeft: '32px' }}>
                                {description}
                              </p>
                              {!parentEnabled && (
                                <p className="ai-submodule-notice">
                                  Enable the <strong>{parentLabel}</strong> module above before activating {label}.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="modules-summary">
                <h3>Modules Summary</h3>
                <p>
                  <strong>Enabled Modules:</strong>{' '}
                  {mainModuleKeys.filter(k => modules[k]).length + 1} of {mainModuleKeys.length + 1}
                </p>
                {modules.bespokeai && (
                  <p style={{ marginTop: '8px' }}>
                    <strong>AI Sub-modules Enabled:</strong>{' '}
                    {AI_SUBMODULES.filter(a => modules[a.key]).length} of {AI_SUBMODULES.length}
                  </p>
                )}
              </div>

              <div className="form-group" style={{ marginTop: '24px', maxWidth: '260px' }}>
                <label>Client Status</label>
                <select value={status} onChange={handleStatusChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
                <p className="form-helper-text" style={{ marginTop: '4px' }}>
                  Saved immediately. Inactive clients show a red health dot in Client Management.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="admins-tab-content">
            <div className="tab-header">
              <h2>Admin Users</h2>
              <button
                onClick={() => setShowAddAdminModal(true)}
                className="edit-urls-button"
                disabled={saving}
              >
                + Add Admin
              </button>
            </div>

            <table className="admins-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Firebase UID</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientData.admins && clientData.admins.length > 0 ? (
                  clientData.admins.map((admin) => (
                    <tr key={admin.uid}>
                      <td>{admin.name}</td>
                      <td>{admin.email}</td>
                      <td><code style={{ fontSize: '11px' }}>{admin.uid}</code></td>
                      <td>
                        <span className="role-badge-config">
                          {admin.role}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => {
                            setSelectedAdmin(admin);
                            setShowEditAdminModal(true);
                          }}
                          className="edit-admin-button"
                          disabled={saving}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin.uid, admin.name)}
                          className="delete-admin-button"
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      No admin users found. Add an admin to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'urls' && (
          <div className="urls-tab-content">
            <div className="tab-header">
              <h2>Portal URLs</h2>
            </div>

            <div className="url-warning-notice">
              <h4>⚠️ Important Notice</h4>
              <p>
                <strong>Editing URLs after setup is not recommended</strong> as the URL is tied to the orgId.
                Changing URLs without updating the orgId will prevent admin, member and volunteer login.
              </p>
              <p>
                If you need to change the organisation's URL, a migration script must be used to update the related values
                in the <code>clients</code>, <code>organizations</code>, <code>staff</code>, <code>members</code> and <code>volunteers</code>
                collections in Firebase.
              </p>
            </div>

            <div className="urls-form">
              <div className="url-field">
                <label>Client Domain:</label>
                <div className="url-display">{clientData.clientDomain || 'Not configured'}</div>
              </div>

              <div className="url-field">
                <label>Admin Login URL:</label>
                <div className="url-display">{clientData.adminLoginURL || 'Not configured'}</div>
              </div>

              <div className="url-field">
                <label>Member Login URL:</label>
                <div className="url-display">{clientData.memberLoginURL || 'Not configured'}</div>
              </div>

              <div className="url-field">
                <label>Volunteer Login URL:</label>
                <div className="url-display">{clientData.volunteerLoginURL || 'Not configured'}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showAddAdminModal && (
        <AdminModal
          title="Add New Admin User"
          onClose={() => setShowAddAdminModal(false)}
          onSubmit={handleAddAdmin}
          loading={saving}
          isEdit={false}
        />
      )}

      {showEditAdminModal && selectedAdmin && (
        <AdminModal
          title="Edit Admin User"
          initialData={selectedAdmin}
          onClose={() => {
            setShowEditAdminModal(false);
            setSelectedAdmin(null);
          }}
          onSubmit={handleEditAdmin}
          loading={saving}
          isEdit={true}
        />
      )}
    </div>
    </div>
  );
}

function AdminModal({ title, initialData, onClose, onSubmit, loading, isEdit }) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    firebaseUID: initialData?.uid || '',
    role: initialData?.role || 'admin',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      permissions: DEFAULT_PERMISSIONS[formData.role] || DEFAULT_PERMISSIONS.manager,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-config">
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="modal-close-button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          {!isEdit && (
            <div className="manual-setup-notice">
              <h4>⚠️ Manual Setup Required</h4>
              <p><strong>Before adding this admin user, you must:</strong></p>
              <ol>
                <li>Go to <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer">Firebase Console</a></li>
                <li>Navigate to <strong>Authentication → Users</strong></li>
                <li>Click <strong>"Add User"</strong></li>
                <li>Enter the email and password for this admin</li>
                <li>Copy the <strong>UID</strong> generated by Firebase</li>
                <li>Paste the UID into the field below</li>
              </ol>
            </div>
          )}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Admin full name"
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="admin@organisation.org"
              disabled={isEdit}
            />
            {isEdit && (
              <p className="form-helper-text">
                📌 Email cannot be changed after creation
              </p>
            )}
          </div>

          {!isEdit && (
            <div className="form-group">
              <label>Firebase UID *</label>
              <input
                type="text"
                value={formData.firebaseUID}
                onChange={(e) => setFormData({ ...formData, firebaseUID: e.target.value })}
                required
                placeholder="Paste the UID from Firebase Console"
              />
              <p className="form-helper-text">
                ℹ️ This is the UID you copied from Firebase Authentication after creating the user
              </p>
            </div>
          )}

          {isEdit && (
            <div className="form-group">
              <label>Firebase UID</label>
              <input
                type="text"
                value={formData.firebaseUID}
                disabled
                className="disabled-field"
              />
              <p className="form-helper-text">
                📌 Firebase UID cannot be changed
              </p>
            </div>
          )}

          <div className="form-group">
            <label>Role</label>
            <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
            <p className="form-helper-text" style={{ marginTop: '4px' }}>
              {ROLE_HELP[formData.role]}
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Saving...' : isEdit ? 'Update Admin' : 'Add Admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientConfigurationPage;
